import React, { useEffect, useRef, useState } from 'react';
import { usePeprisGame } from './PeprisCore';
import InstructionsDialog from './InstructionsDialog';
import { BASE_URL } from './constants';
import Hammer from 'hammerjs';

const PeprisMobile = () => {
  const {
    gameState,
    playerInfo,
    setPlayerInfo,
    isSoundOn,
    audioRefs,
    moveDown,
    moveHorizontally,
    rotatePepper,
    changeCurrentPepperColor,
    startGame,
    resetGame,
    toggleSound,
    saveHighScore,
    highScores
  } = usePeprisGame();

  const touchRef = useRef(null);
  const boardContainerRef = useRef(null);
  const [boardSize, setBoardSize] = useState({ width: 0, height: 0 });

  // Touch controls setup
  useEffect(() => {
    if (touchRef.current) {
      const hammer = new Hammer(touchRef.current);
      hammer.get('swipe').set({ 
        direction: Hammer.DIRECTION_ALL,
        threshold: 5,
        velocity: 0.1
      });

      hammer.on('swipeleft', () => moveHorizontally(-1));
      hammer.on('swiperight', () => moveHorizontally(1));
      hammer.on('swipedown', () => moveDown());
      hammer.on('tap', () => rotatePepper());

      return () => hammer.destroy();
    }
  }, [moveHorizontally, moveDown, rotatePepper]);

  // Calculate optimal board size
  useEffect(() => {
    const calculateBoardSize = () => {
      if (!boardContainerRef.current) return;

      const windowHeight = window.innerHeight;
      const headerHeight = 60; // Header height
      const controlsHeight = 180; // Controls height including padding
      const availableHeight = windowHeight - headerHeight - controlsHeight;
      
      // Calculate width based on screen width
      const maxWidth = Math.min(window.innerWidth * 0.95, 400);
      
      // Calculate height while maintaining 2:1 aspect ratio
      const height = Math.min(availableHeight, maxWidth * 2);
      // Adjust width to maintain aspect ratio if height is constrained
      const width = height / 2;

      setBoardSize({ width, height });
    };

    calculateBoardSize();
    window.addEventListener('resize', calculateBoardSize);
    return () => window.removeEventListener('resize', calculateBoardSize);
  }, []);

  const renderMobileCell = React.useCallback((cellValue, rowIndex, colIndex) => {
    let cellClass = 'w-full h-full relative border border-gray-800 ';

    if (cellValue && cellValue !== 'white') {
      cellClass += cellValue;
    } else {
      cellClass += 'bg-gray-900';
    }

    if (gameState.currentPepper && 
        rowIndex >= gameState.currentPepper.y && 
        rowIndex < gameState.currentPepper.y + gameState.currentPepper.shape.length &&
        colIndex >= gameState.currentPepper.x && 
        colIndex < gameState.currentPepper.x + gameState.currentPepper.shape[0].length &&
        gameState.currentPepper.shape[rowIndex - gameState.currentPepper.y][colIndex - gameState.currentPepper.x]) {
      cellClass = `w-full h-full relative border border-gray-800 ${gameState.currentPepper.color} z-10`;
    }

    return (
      <div 
        key={`${rowIndex}-${colIndex}`} 
        className={cellClass}
      />
    );
  }, [gameState.currentPepper]);

  const renderNextPepperMobile = React.useCallback(() => {
    if (!gameState.nextPepper) return null;
    
    return (
      <div className="bg-black bg-opacity-20 rounded-lg p-2">
        <div className="grid gap-0.5"
          style={{ 
            gridTemplateColumns: `repeat(${gameState.nextPepper.shape[0].length}, 1fr)`
          }}
        >
          {gameState.nextPepper.shape.map((row, rowIndex) =>
            row.map((cell, colIndex) => (
              <div
                key={`next-${rowIndex}-${colIndex}`}
                className={`w-4 h-4 ${cell ? gameState.nextPepper.color : 'bg-transparent'}`}
              />
            ))
          )}
        </div>
      </div>
    );
  }, [gameState.nextPepper]);

  const renderMobileGameOver = React.useCallback(() => (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 w-full max-w-sm">
        <h2 className="text-2xl font-bold mb-4">Game Over!</h2>
        <p className="text-xl mb-4">Score: {gameState.score}</p>
        <input
          type="text"
          placeholder="Your Name"
          value={playerInfo.name}
          onChange={(e) => setPlayerInfo(prev => ({ ...prev, name: e.target.value }))}
          className="w-full p-3 mb-2 border rounded-lg text-lg"
        />
        <input
          type="email"
          placeholder="Your Email"
          value={playerInfo.email}
          onChange={(e) => setPlayerInfo(prev => ({ ...prev, email: e.target.value }))}
          className="w-full p-3 mb-2 border rounded-lg text-lg"
        />
        <input
          type="tel"
          placeholder="Your Phone"
          value={playerInfo.phone}
          onChange={(e) => setPlayerInfo(prev => ({ ...prev, phone: e.target.value }))}
          className="w-full p-3 mb-4 border rounded-lg text-lg"
        />
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={saveHighScore}
            className="bg-green-500 text-white p-4 rounded-lg text-lg font-medium active:bg-green-600 active:scale-95 transition-all"
          >
            Save Score
          </button>
          <button
            onClick={resetGame}
            className="bg-blue-500 text-white p-4 rounded-lg text-lg font-medium active:bg-blue-600 active:scale-95 transition-all"
          >
            Play Again
          </button>
        </div>
      </div>
    </div>
  ), [gameState.score, playerInfo, setPlayerInfo, saveHighScore, resetGame]);

  return (
    <div className="md:hidden min-h-screen bg-gradient-to-b from-red-100 to-yellow-100 flex flex-col">
      {/* Compact Header */}
      <div className="bg-white bg-opacity-90 shadow-md p-2 z-50">
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center space-x-2">
            <div className="text-base font-semibold">Score: {gameState.score}</div>
            <div className="text-base font-semibold">Lvl: {gameState.level}</div>
          </div>
          {renderNextPepperMobile()}
          <button
            onClick={toggleSound}
            aria-label={isSoundOn ? "Mute sound" : "Unmute sound"}
            className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-700">
              <polygon points="3 9 7 9 12 4 12 20 7 15 3 15" fill="currentColor" stroke="none" />
              {isSoundOn ? (
                <>
                  <path d="M16 8a5 5 0 0 1 0 8" />
                  <path d="M18.5 5.5a9 9 0 0 1 0 13" />
                </>
              ) : (
                <>
                  <line x1="16" y1="9" x2="22" y2="15" />
                  <line x1="22" y1="9" x2="16" y2="15" />
                </>
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Game Board */}
      <div className="flex-1 flex items-center justify-center px-2 py-1">
        <div 
          ref={boardContainerRef}
          className="relative w-full flex items-center justify-center"
        >
          <div
            ref={touchRef}
            className="bg-black rounded-lg overflow-hidden touch-none"
            style={{ 
              width: `${boardSize.width}px`,
              height: `${boardSize.height}px`
            }}
          >
            <div 
              className="grid h-full" 
              style={{ 
                gridTemplateColumns: 'repeat(10, 1fr)', 
                gridTemplateRows: 'repeat(20, 1fr)',
                gap: '1px' 
              }}
            >
              {gameState.board.map((row, rowIndex) => (
                <React.Fragment key={rowIndex}>
                  {row.map((cell, colIndex) => renderMobileCell(cell, rowIndex, colIndex))}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Compact Controls */}
      <div className="bg-white bg-opacity-90 shadow-lg p-3">
        <div className="max-w-sm mx-auto">
          <div className="grid grid-cols-3 gap-3 mb-3">
            <button
              onClick={() => moveHorizontally(-1)}
              aria-label="Move left"
              className="bg-blue-500 rounded-xl p-3 active:bg-blue-600 active:scale-95 transition-all flex items-center justify-center"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                <polyline points="15 6 9 12 15 18" />
              </svg>
            </button>
            <button
              onClick={moveDown}
              aria-label="Move down"
              className="bg-blue-500 rounded-xl p-3 active:bg-blue-600 active:scale-95 transition-all flex items-center justify-center"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            <button
              onClick={() => moveHorizontally(1)}
              aria-label="Move right"
              className="bg-blue-500 rounded-xl p-3 active:bg-blue-600 active:scale-95 transition-all flex items-center justify-center"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                <polyline points="9 6 15 12 9 18" />
              </svg>
            </button>
          </div>

          <div className="flex justify-center gap-3">
            <button
              onClick={rotatePepper}
              aria-label="Rotate"
              className="bg-purple-500 rounded-full w-12 h-12 flex items-center justify-center active:bg-purple-600 active:scale-95 transition-all"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                <path d="M21 12a9 9 0 1 1-3-6.7" />
                <polyline points="21 3 21 7 17 7" />
              </svg>
            </button>
            <button
              onClick={changeCurrentPepperColor}
              aria-label="Change color"
              className="bg-green-500 rounded-full w-12 h-12 flex items-center justify-center active:bg-green-600 active:scale-95 transition-all"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                <path d="M12 3a9 9 0 0 0 0 18c1.1 0 2-.9 2-2 0-.5-.2-1-.5-1.3-.3-.4-.5-.8-.5-1.2 0-1 .9-1.5 1.5-1.5H17a4 4 0 0 0 4-4c0-4.4-4-8-9-8z" />
                <circle cx="7.5" cy="11.5" r="1" fill="currentColor" stroke="none" />
                <circle cx="10.5" cy="7.5" r="1" fill="currentColor" stroke="none" />
                <circle cx="15.5" cy="7.5" r="1" fill="currentColor" stroke="none" />
                <circle cx="17.5" cy="11.5" r="1" fill="currentColor" stroke="none" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Rest of the components */}
      <audio ref={audioRefs.background} src={`${BASE_URL}/sounds/background.mp3`} loop />
      <audio ref={audioRefs.row} src={`${BASE_URL}/sounds/row.m4a`} />
      <audio ref={audioRefs.buzz} src={`${BASE_URL}/sounds/buzz.m4a`} />
      <audio ref={audioRefs.clock} src={`${BASE_URL}/sounds/clock.mp3`} />
      <audio ref={audioRefs.bonus} src={`${BASE_URL}/sounds/bonus.mp3`} />

      {gameState.gameOver && renderMobileGameOver()}
      
      <InstructionsDialog
        isOpen={gameState.showInstructions}
        onClose={startGame}
        isMobile={true}
      />
    </div>
  );
};

export default PeprisMobile;